import { useEffect } from 'react';
import { store, useHistoryFlags, useStore } from '@/state/store';
import { checkAIAvailability, generateDesigns } from '@/engine/pipeline';
import { ImageUploader } from '@/components/ImageUploader';
import { TextEditor } from '@/components/TextEditor';
import { DesignStyleSelector } from '@/components/DesignStyleSelector';
import { LogoUploader } from '@/components/LogoUploader';
import { CanvasEditor } from '@/components/CanvasEditor';
import { AIDesignGenerator } from '@/components/AIDesignGenerator';
import { FormatSelector } from '@/components/FormatSelector';
import { TypographySelector } from '@/components/TypographySelector';
import { ControlsPanel } from '@/components/ControlsPanel';
import { ColorAnalyzer } from '@/components/ColorAnalyzer';
import { FaceDetectorPanel } from '@/components/FaceDetectorPanel';
import { LayerPanel } from '@/components/LayerPanel';
import { ExportPanel } from '@/components/ExportPanel';
import { RationalePanel } from '@/components/RationalePanel';
import { Toggle } from '@/components/ui/Controls';

export default function App() {
  const status = useStore((s) => s.status);
  const statusMessage = useStore((s) => s.statusMessage);
  const aiConfigured = useStore((s) => s.aiConfigured);
  const preferAI = useStore((s) => s.preferAI);
  const { canUndo, canRedo } = useHistoryFlags();

  // Ask the proxy once whether a real vision model is reachable. The app works
  // either way; this only changes which engine composes.
  useEffect(() => {
    void checkAIAvailability();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target && /^(INPUT|TEXTAREA)$/.test(target.tagName);
      const mod = event.metaKey || event.ctrlKey;

      if (mod && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) store.redo();
        else store.undo();
        return;
      }
      if (mod && event.key === 'Enter') {
        event.preventDefault();
        void generateDesigns();
        return;
      }
      if (event.key === 'Escape' && !typing) {
        store.set({ selectedBlockId: null });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex h-full flex-col bg-ink-950">
      <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-white/[0.06] px-4 py-2.5">
        <div className="flex items-baseline gap-2">
          <h1 className="text-sm font-medium tracking-tight text-ink-100">Textdesigner</h1>
          <span className="hidden text-[10px] text-ink-500 sm:inline">
            photo + headline → composed graphic
          </span>
        </div>

        <div className="mx-auto hidden md:block">
          <FormatSelector />
        </div>

        <div className="ml-auto flex items-center gap-2">
          <div className="hidden lg:block">
            <Toggle
              label="Use Claude vision"
              checked={preferAI && aiConfigured}
              onChange={(v) => store.set({ preferAI: v })}
              hint={
                aiConfigured
                  ? 'Vision model reachable'
                  : 'No API key — built-in engine'
              }
            />
          </div>
          <div className="flex gap-1">
            <button
              type="button"
              className="btn-ghost px-2"
              disabled={!canUndo}
              onClick={() => store.undo()}
              title="Undo (⌘Z)"
              aria-label="Undo"
            >
              ↶
            </button>
            <button
              type="button"
              className="btn-ghost px-2"
              disabled={!canRedo}
              onClick={() => store.redo()}
              title="Redo (⇧⌘Z)"
              aria-label="Redo"
            >
              ↷
            </button>
          </div>
        </div>
      </header>

      <div className="border-b border-white/[0.06] px-4 py-2 md:hidden">
        <FormatSelector />
      </div>

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto p-3 xl:flex-row xl:overflow-hidden">
        {/* Left: inputs */}
        <aside className="scroll-slim flex w-full shrink-0 flex-col gap-3 xl:w-[300px] xl:overflow-y-auto xl:pr-1">
          <ImageUploader />
          <TextEditor />
          <DesignStyleSelector />
          <LogoUploader />
        </aside>

        {/* Center: canvas */}
        <section className="flex min-h-[420px] min-w-0 flex-1 flex-col gap-3">
          <div className="panel flex min-h-0 flex-1 items-center justify-center p-4">
            <CanvasEditor />
          </div>
          <div className="panel shrink-0 p-3">
            <AIDesignGenerator />
          </div>
          <div
            className={`shrink-0 rounded-lg px-3 py-2 text-[11px] ${
              status === 'error'
                ? 'bg-red-500/10 text-red-300'
                : 'bg-white/[0.03] text-ink-400'
            }`}
            role="status"
            aria-live="polite"
          >
            {statusMessage}
          </div>
        </section>

        {/* Right: controls */}
        <aside className="scroll-slim flex w-full shrink-0 flex-col gap-3 xl:w-[300px] xl:overflow-y-auto xl:pl-1">
          <TypographySelector />
          <ControlsPanel />
          <ColorAnalyzer />
          <FaceDetectorPanel />
          <LayerPanel />
          <RationalePanel />
          <ExportPanel />
        </aside>
      </main>
    </div>
  );
}
